import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Folder, 
  FileSpreadsheet, 
  FileText,
  ChevronRight, 
  Download, 
  ArrowLeft,
  Cloud,
  Loader2,
  RefreshCw,
  AlertCircle,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API } from '../config/api';

const DriveImporter = ({ onImportSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPath, setCurrentPath] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderContents, setFolderContents] = useState({ folders: [], files: [] });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, ok: 0, failed: 0 });
  const [apiConfigured, setApiConfigured] = useState(true);

  // Extract folder ID from Google Drive link
  const extractFolderIdFromLink = (link) => {
    if (!link) return null;
    
    // Match patterns like:
    // https://drive.google.com/drive/folders/FOLDER_ID
    // https://drive.google.com/drive/folders/FOLDER_ID?usp=sharing
    const folderMatch = link.match(/\/folders\/([a-zA-Z0-9_-]+)/);
    if (folderMatch) return folderMatch[1];
    
    // Match pattern: https://drive.google.com/open?id=FOLDER_ID
    const openMatch = link.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (openMatch) return openMatch[1];
    
    return null;
  };

  useEffect(() => {
    checkStatusAndFetchCategories();
  }, []);

  const checkStatusAndFetchCategories = async () => {
    try {
      const statusRes = await axios.get(`${API}/drive/status`);
      setApiConfigured(statusRes.data.configured);
      
      if (statusRes.data.configured) {
        const catRes = await axios.get(`${API}/drive/categories`);
        setCategories(catRes.data.categories);
      }
    } catch (error) {
      console.error('Error checking Drive status:', error);
      setApiConfigured(false);
    }
  };

  // Check if a service is browseable (has API support)
  const isServiceBrowseable = (folderId) => {
    if (!folderId) return false;
    // IceDrive, Dropbox, etc. are not browseable via API
    const nonBrowseableServices = ['icedrive.net', 'dropbox.com', 'onedrive.', 'mega.nz', 'box.com'];
    const folderIdLower = folderId.toLowerCase();
    return !nonBrowseableServices.some(service => folderIdLower.includes(service));
  };

  // Files that contain lap-by-lap telemetry data suitable for analysis.
  // We only mark a file as "Analizable" when its filename explicitly signals
  // per-lap detail (e.g. "vtaxvta", "vta x vta", "vueltas", "lap x lap", etc.).
  // Everything else (grids, regulations, generic PDFs, entry lists, results
  // summaries) can still be opened via "Visualizar" but must NOT trigger the
  // analysis parser — otherwise the backend crashes on missing sector data.
  //
  // Naming variants observed in real Drive folders:
  //   • vtas / vueltas / vueltasxvueltas
  //   • vtaxvta / vta x vta / vta_x_vta / VtaXVta
  //   • vueltaxvuelta / vuelta x vuelta / vuelta por vuelta / vta por vta
  //   • lap x lap / lapxlap / lap by lap / lapbylap
  const isAnalyzable = (fileName) => {
    if (!fileName) return false;
    const n = fileName.toLowerCase();
    // Only XLS/XLSX and PDF files can be parsed by the backend importer.
    const isSupportedExt =
      n.endsWith('.xls') || n.endsWith('.xlsx') || n.endsWith('.pdf');
    if (!isSupportedExt) return false;
    // Normalize: strip separators (spaces, underscores, dashes, dots) so that
    // "vta x vta", "vta_x_vta", "VtaXVta" and "vtaxvta" all collapse to the
    // same token before matching.
    const compact = n.replace(/[\s_\-.]+/g, '');
    const lapKeywords = [
      'vtas',
      'vueltas',
      'vtaxvta',
      'vueltaxvuelta',
      'vueltaporvuelta',
      'vtaporvta',
      'lapxlap',
      'lapbylap',
      'lapporlap',
    ];
    return lapKeywords.some((k) => compact.includes(k));
  };

  const selectCategory = async (category) => {
    setSelectedCategory(category);
    
    // Check if this service can be browsed
    if (!isServiceBrowseable(category.folder_id)) {
      // For non-browseable services, just set the category but don't try to load folders
      setCurrentPath([{ id: category.folder_id, name: category.name }]);
      setCurrentFolderId(category.folder_id);
      setFolderContents({ folders: [], files: [] });
      return;
    }
    
    setCurrentPath([{ id: category.folder_id, name: category.name }]);
    setCurrentFolderId(category.folder_id);
    await loadFolder(category.folder_id);
  };

  const loadFolder = async (folderId) => {
    // Don't try to load non-browseable services
    if (!isServiceBrowseable(folderId)) {
      setFolderContents({ folders: [], files: [] });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/drive/folders/${folderId}`);
      setFolderContents(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar carpeta');
      setFolderContents({ folders: [], files: [] });
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = async (folder) => {
    setCurrentPath([...currentPath, { id: folder.id, name: folder.name }]);
    setCurrentFolderId(folder.id);
    await loadFolder(folder.id);
  };

  const navigateBack = async (index) => {
    const newPath = currentPath.slice(0, index + 1);
    setCurrentPath(newPath);
    const targetFolder = newPath[newPath.length - 1];
    setCurrentFolderId(targetFolder.id);
    await loadFolder(targetFolder.id);
  };

  const goToRoot = () => {
    setSelectedCategory(null);
    setCurrentPath([]);
    setCurrentFolderId(null);
    setFolderContents({ folders: [], files: [] });
  };

  // Compute the (fecha, clase) breadcrumb fields based on current path.
  // Extracted so we can reuse it in single-file and bulk-import flows.
  const breadcrumbContext = () => {
    let fecha = '';
    let clase = '';
    const fechaParts = [];
    for (let i = 1; i < currentPath.length; i++) {
      const pathName = currentPath[i].name;
      if (pathName.toLowerCase().includes('clase')) {
        clase = pathName;
      } else if (!['RESULTADOS', 'FINALES', 'SERIES', 'ENTRENAMIENTOS'].includes(pathName.toUpperCase())) {
        fechaParts.push(pathName);
      }
    }
    fecha = fechaParts.join(' - ');
    if (!clase && folderContents.parent_name?.toLowerCase().includes('clase')) {
      clase = folderContents.parent_name;
    }
    return { fecha, clase };
  };

  // Core single-file import. Returns true on success, false on failure.
  // Set `silent=true` to skip per-file toasts (used by bulk import).
  const _doImport = async (file, silent = false) => {
    const { fecha, clase } = breadcrumbContext();
    try {
      const response = await axios.post(`${API}/drive/import`, {
        file_id: file.id,
        file_name: file.name,
        category: selectedCategory?.id || 'turismo_pista',
        fecha,
        clase,
      });
      try {
        await axios.post(`${API}/sync/mark-known/${file.id}`);
      } catch (e) {
        // Non-fatal — file just won't be marked as already-seen.
      }
      if (!silent) toast.success(`✓ ${response.data.message}`);
      if (onImportSuccess) onImportSuccess(response.data);
      return true;
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.detail || 'Error al importar archivo');
      return false;
    }
  };

  const importFile = async (file) => {
    setImporting(file.id);
    try {
      await _doImport(file, false);
    } finally {
      setImporting(null);
    }
  };

  // Bulk-import every analyzable file in the current folder, sequentially.
  // Sequential (not parallel) so the backend doesn't get hammered, and so
  // toast/progress feedback is predictable.
  const importAll = async () => {
    const targets = (folderContents.files || []).filter((f) => isAnalyzable(f.name));
    if (targets.length === 0) {
      toast.info('No hay archivos analizables en esta carpeta');
      return;
    }
    if (!window.confirm(`¿Importar las ${targets.length} planillas analizables de esta carpeta?`)) return;
    setBulkImporting(true);
    setBulkProgress({ done: 0, total: targets.length, ok: 0, failed: 0 });
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < targets.length; i++) {
      const f = targets[i];
      setImporting(f.id);
      const success = await _doImport(f, true);
      if (success) ok += 1; else failed += 1;
      setBulkProgress({ done: i + 1, total: targets.length, ok, failed });
    }
    setImporting(null);
    setBulkImporting(false);
    if (failed === 0) {
      toast.success(`✓ ${ok} archivos importados correctamente`);
    } else {
      toast.warning(`Importación finalizada: ${ok} OK, ${failed} con error`);
    }
  };

  // API not configured - show message
  if (!apiConfigured) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-12 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
          <p className="text-gray-400">Importación desde la nube no disponible</p>
          <p className="text-sm text-gray-500 mt-2">Contacta al administrador</p>
        </CardContent>
      </Card>
    );
  }

  // Category selection
  if (!selectedCategory) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <CardTitle className="text-xl text-white flex items-center gap-2">
            <Cloud className="w-5 h-5 text-red-500" />
            Importar Tiempos
          </CardTitle>
          <CardDescription className="text-gray-400">
            Selecciona una categoría para ver los tiempos disponibles
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {categories.map((category) => (
              <div key={category.id}>
                {/* Category header row (list style) */}
                <button
                  onClick={() => selectCategory(category)}
                  className="w-full flex items-center gap-3 px-3 py-2 border-b border-red-900/40 hover:border-red-500 transition-colors text-left group"
                  data-testid={`category-${category.id}`}
                >
                  <Folder className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
                    <h3 className="text-white font-semibold text-sm uppercase tracking-wide">
                      {category.name}
                    </h3>
                    <span className="text-xs text-gray-500 truncate">
                      {category.description}
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-500 group-hover:text-red-400 transition-colors hidden sm:inline">
                    Explorar
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-red-400 transition-colors" />
                </button>

                {/* Championship Rounds as list rows */}
                {category.rounds && category.rounds.length > 0 && (
                  <ul className="divide-y divide-neutral-800/70">
                    {category.rounds.map((round) => (
                      <li key={round.round}>
                        <button
                          onClick={() => {
                            if (round.drive_link) {
                              const folderId = extractFolderIdFromLink(round.drive_link);
                              if (folderId) {
                                setSelectedCategory({
                                  ...category,
                                  folder_id: folderId,
                                  selectedRound: round
                                });
                                setCurrentPath([{ id: folderId, name: round.name }]);
                                setCurrentFolderId(folderId);
                                loadFolder(folderId);
                              } else {
                                window.open(round.drive_link, '_blank');
                              }
                            }
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-red-950/20 transition-colors text-left"
                          data-testid={`round-${category.id}-${round.round}`}
                        >
                          <span className="w-6 h-6 rounded bg-red-600/20 text-red-400 text-xs flex items-center justify-center font-bold flex-shrink-0">
                            {round.round}
                          </span>
                          <span className="flex-1 min-w-0 text-gray-200 text-sm truncate">
                            {round.name}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {categories.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <Cloud className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Cargando categorías...</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Folder browser
  return (
    <Card className="bg-neutral-900 border-red-900/30">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl text-white flex items-center gap-2">
            <Cloud className="w-5 h-5 text-red-500" />
            {selectedCategory.name}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => loadFolder(currentFolderId)}
            className="text-gray-400 hover:text-white"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-sm mt-2 flex-wrap">
          <button
            onClick={goToRoot}
            className="text-gray-400 hover:text-red-400 transition-colors"
          >
            Categorías
          </button>
          {currentPath.map((item, index) => (
            <React.Fragment key={item.id}>
              <ChevronRight className="w-4 h-4 text-gray-600" />
              <button
                onClick={() => navigateBack(index)}
                className={`transition-colors ${
                  index === currentPath.length - 1 
                    ? 'text-red-400 font-medium' 
                    : 'text-gray-400 hover:text-red-400'
                }`}
              >
                {item.name}
              </button>
            </React.Fragment>
          ))}
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Special UI for non-browseable services (IceDrive, etc.) */}
        {!isServiceBrowseable(currentFolderId) ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Cloud className="w-8 h-8 text-blue-500" />
            </div>
            <h3 className="text-white font-semibold text-lg mb-2">
              Carpeta Externa (IceDrive)
            </h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Este servicio no permite navegación directa. Descarga los archivos manualmente y súbelos usando el botón de abajo.
            </p>
            
            {/* Open in browser button */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => window.open(currentFolderId, '_blank')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Cloud className="w-4 h-4 mr-2" />
                Abrir en IceDrive
              </Button>
              <Button
                variant="outline"
                onClick={goToRoot}
                className="border-gray-600 text-gray-300 hover:bg-neutral-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver a Categorías
              </Button>
            </div>
            
            {/* Instructions */}
            <div className="mt-8 p-4 bg-neutral-800/50 rounded-lg text-left max-w-md mx-auto">
              <p className="text-sm text-gray-400 mb-2 font-medium">Instrucciones:</p>
              <ol className="text-sm text-gray-500 space-y-1 list-decimal list-inside">
                <li>Clic en &quot;Abrir en IceDrive&quot;</li>
                <li>Descarga el archivo XLS o PDF</li>
                <li>Vuelve aquí y usa &quot;Subir Archivo&quot; en la pestaña principal</li>
              </ol>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
          </div>
        ) : (
          <div className="space-y-2">
            {/* Bulk import + Back */}
            {(() => {
              const analyzableCount = (folderContents.files || []).filter((f) => isAnalyzable(f.name)).length;
              const showBack = currentPath.length > 1 && isServiceBrowseable(currentPath[currentPath.length - 2]?.id);
              if (analyzableCount === 0 && !showBack) return null;
              return (
                <div className="flex items-center gap-2 flex-wrap">
                  {showBack && (
                    <button
                      onClick={() => navigateBack(currentPath.length - 2)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-800/50 transition-colors text-gray-400 text-sm"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Volver</span>
                    </button>
                  )}
                  <div className="flex-1" />
                  {analyzableCount >= 2 && (
                    <Button
                      size="sm"
                      onClick={importAll}
                      disabled={bulkImporting || importing !== null}
                      data-testid="import-all-btn"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {bulkImporting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                          Importando {bulkProgress.done}/{bulkProgress.total}
                          {bulkProgress.failed > 0 && (
                            <span className="ml-1.5 text-yellow-200">({bulkProgress.failed} fallidas)</span>
                          )}
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-1.5" />
                          Importar todas ({analyzableCount})
                        </>
                      )}
                    </Button>
                  )}
                </div>
              );
            })()}

            {/* Folders */}
            {folderContents.folders.map((folder) => (
              <button
                key={folder.id}
                onClick={() => navigateToFolder(folder)}
                className="flex items-center gap-3 w-full p-3 rounded-lg bg-neutral-800/30 hover:bg-neutral-800/60 border border-transparent hover:border-red-900/50 transition-all text-left"
                data-testid={`folder-${folder.name}`}
              >
                <div className="w-10 h-10 bg-yellow-600/20 rounded-lg flex items-center justify-center">
                  <Folder className="w-5 h-5 text-yellow-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{folder.name}</p>
                  {folder.modified_time && (
                    <p className="text-xs text-gray-500">
                      {new Date(folder.modified_time).toLocaleDateString('es-AR')}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </button>
            ))}
            
            {/* Files */}
            {folderContents.files.map((file) => {
              const isPdf = file.name.toLowerCase().endsWith('.pdf');
              const isXls = file.name.toLowerCase().endsWith('.xls') || file.name.toLowerCase().endsWith('.xlsx');
              const analyzable = isAnalyzable(file.name);
              const viewUrl = `https://drive.google.com/file/d/${file.id}/view`;
              
              return (
                <div
                  key={file.id}
                  className={`flex items-center gap-3 w-full p-3 rounded-lg bg-neutral-800/30 border border-transparent transition-all ${
                    analyzable
                      ? (isPdf ? 'hover:border-blue-900/50' : 'hover:border-green-900/50')
                      : 'hover:border-neutral-600 opacity-80'
                  }`}
                  data-testid={`file-${file.name}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    !analyzable
                      ? 'bg-neutral-700/40'
                      : (isPdf ? 'bg-blue-600/20' : 'bg-green-600/20')
                  }`}>
                    {isPdf ? (
                      <FileText className={`w-5 h-5 ${!analyzable ? 'text-gray-400' : 'text-blue-500'}`} />
                    ) : (
                      <FileSpreadsheet className={`w-5 h-5 ${!analyzable ? 'text-gray-400' : 'text-green-500'}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-medium truncate ${analyzable ? 'text-white' : 'text-gray-300'}`}>{file.name}</p>
                      {analyzable && isXls && (
                        <Badge className="bg-green-600/20 text-green-400 text-[10px] px-1.5 py-0">
                          Recomendado
                        </Badge>
                      )}
                      {!analyzable && (
                        <Badge className="bg-neutral-700/60 text-gray-300 text-[10px] px-1.5 py-0">
                          Solo vista
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {isPdf ? 'PDF' : 'XLS'}
                      {file.size && ` • ${(parseInt(file.size) / 1024).toFixed(1)} KB`}
                      {file.modified_time && ` • ${new Date(file.modified_time).toLocaleDateString('es-AR')}`}
                    </p>
                  </div>
                  {analyzable ? (
                    <Button
                      size="sm"
                      onClick={() => importFile(file)}
                      disabled={importing === file.id}
                      className={isPdf 
                        ? "bg-blue-600 hover:bg-blue-700 text-white" 
                        : "bg-green-600 hover:bg-green-700 text-white"
                      }
                      data-testid={`import-file-${file.name}`}
                    >
                      {importing === file.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-1" />
                          Analizar
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(viewUrl, '_blank', 'noopener,noreferrer')}
                      className="border-neutral-600 text-gray-200 hover:bg-neutral-800 hover:text-white"
                      data-testid={`view-file-${file.name}`}
                      title="Abrir en Google Drive"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Visualizar
                    </Button>
                  )}
                </div>
              );
            })}
            
            {/* Empty state */}
            {folderContents.folders.length === 0 && folderContents.files.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <Folder className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Carpeta vacía</p>
                <p className="text-sm text-gray-500 mt-1">No hay archivos XLS en esta carpeta</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DriveImporter;
